import { TFunction } from 'i18next';
import { DiarySummary, HeadacheDaysAnswer, UnloggedAnswer } from './types';

export const MIN_LOGGED_DAYS = 30;

export type DiaryState = DiarySummary | 'loading' | 'failed';

export type HeadacheDaysView =
  | 'loading'
  | 'plain'
  | 'sparse'
  | 'diary'
  | 'full';

export function headacheDaysView(diary: DiaryState): HeadacheDaysView {
  if (diary === 'loading') return 'loading';
  if (diary === 'failed' || diary.loggedDays === 0) return 'plain';
  if (diary.loggedDays < MIN_LOGGED_DAYS) return 'sparse';
  if (diary.loggedDays === diary.windowDays) return 'full';
  return 'diary';
}

export function unloggedDays(summary: DiarySummary): number {
  return summary.windowDays - summary.loggedDays;
}

export function formatDays(count: number): string {
  if (count === 1) return '1 dag';
  return `${count} dagar`;
}

export function plainAnswer(
  total: number,
  windowDays: number,
  diary: DiaryState
): HeadacheDaysAnswer {
  if (typeof diary === 'object') {
    return {
      mode: 'plain',
      windowStart: diary.windowStart,
      windowEnd: diary.windowEnd,
      windowDays,
      loggedDays: diary.loggedDays,
      headacheDaysDiary: diary.headacheDays,
      headacheDaysTotal: total,
      isPatientEstimate: true,
      diaryLoadFailed: false,
    };
  }
  return {
    mode: 'plain',
    windowDays,
    headacheDaysTotal: total,
    isPatientEstimate: true,
    diaryLoadFailed: diary === 'failed',
  };
}

export function diaryAnswer(
  summary: DiarySummary,
  choice?: UnloggedAnswer,
  total?: number
): HeadacheDaysAnswer {
  const base = {
    mode: 'diary' as const,
    windowStart: summary.windowStart,
    windowEnd: summary.windowEnd,
    windowDays: summary.windowDays,
    loggedDays: summary.loggedDays,
    headacheDaysDiary: summary.headacheDays,
    unloggedAnswer: choice,
    diaryLoadFailed: false,
  };
  if (choice === 'yes') {
    return {
      ...base,
      headacheDaysTotal: total,
      isLowerBound: false,
      isPatientEstimate: true,
    };
  }
  return {
    ...base,
    headacheDaysTotal: summary.headacheDays,
    isLowerBound: choice === 'unsure',
    isPatientEstimate: false,
  };
}

export function totalHelperText(
  value: string,
  summary: DiarySummary,
  t: TFunction
): string | null {
  if (value === '') return null;
  if (!/^\d+$/.test(value)) {
    return t('headacheDaysQuestion.wholeNumber');
  }

  const total = Number(value);
  const h = summary.headacheDays;
  const u = unloggedDays(summary);
  if (total <= h) {
    return t('headacheDaysQuestion.totalNotAboveDiary', { count: h });
  }
  if (total > h + u) {
    return t('headacheDaysQuestion.totalAboveMax', {
      count: h + u,
      diary: t('headacheDaysQuestion.inDiary', { count: h }),
      unlogged: t('headacheDaysQuestion.unloggedCount', { count: u }),
    });
  }
  return null;
}

export function plainHelperText(
  value: string,
  windowDays: number,
  t: TFunction
): string | null {
  if (value === '') return null;
  if (!/^\d+$/.test(value)) {
    return t('headacheDaysQuestion.wholeNumber');
  }
  if (Number(value) > windowDays) {
    return t('headacheDaysQuestion.plainAboveMax', { max: windowDays });
  }
  return null;
}

export function isPlainWithoutDiary(answer?: HeadacheDaysAnswer): boolean {
  return answer?.mode === 'plain' && !answer.loggedDays;
}

export function isStaleAnswer(
  saved: HeadacheDaysAnswer,
  summary: DiarySummary
): boolean {
  if (isPlainWithoutDiary(saved)) return false;
  if (saved.windowEnd && saved.windowEnd !== summary.windowEnd) return true;
  return (
    saved.windowDays !== summary.windowDays ||
    (saved.loggedDays ?? 0) !== summary.loggedDays ||
    (saved.headacheDaysDiary ?? 0) !== summary.headacheDays
  );
}

export async function diaryForAnswer(
  saved: HeadacheDaysAnswer | undefined,
  fetchSummary: (windowEnd?: string) => Promise<DiarySummary>
): Promise<DiarySummary> {
  if (saved?.windowEnd) {
    const savedWindow = await fetchSummary(saved.windowEnd);
    if (!isStaleAnswer(saved, savedWindow)) return savedWindow;
  }
  return fetchSummary();
}
